/**
 * API functions for coach template management.
 * WorkoutTemplate, PlanTemplate, PlanInstance, KnowledgeEntry, WeekTemplate CRUD.
 *
 * Raw api.* calls are centralized in typedClient.ts (coachTemplatesApi).
 * This file re-exports named functions for backward compat with hooks.
 */

import { coachTemplatesApi } from './api/typedClient';
import { log } from './logger';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface WorkoutSegmentResponse {
  id: string;
  workout_template_id: string;
  sort_order: number;
  type: string | null;
  name: string | null;
  duration: string | null;
  distance: string | null;
  intensity: string | null;
  target_pace: string | null;
  target_hr: string | null;
  target_power: string | null;
  rest: string | null;
  notes: string | null;
}

export interface WorkoutTemplateResponse {
  id: string;
  coach_id: string;
  name: string;
  sport: string;
  stimulus: string | null;
  duration: string | null;
  difficulty: string | null;
  tags: string[];
  notes: string | null;
  source: string;
  segments: WorkoutSegmentResponse[];
  created_at: string;
  updated_at: string;
}

export interface WorkoutTemplateListResponse {
  items: WorkoutTemplateResponse[];
  total: number;
}

export interface WorkoutTemplateCreate {
  name: string;
  sport: string;
  stimulus?: string;
  duration?: string;
  difficulty?: string;
  tags?: string[];
  notes?: string;
  segments?: Partial<WorkoutSegmentResponse>[];
}

export interface WorkoutTemplateUpdate {
  name?: string;
  sport?: string;
  stimulus?: string;
  duration?: string;
  difficulty?: string;
  tags?: string[];
  notes?: string;
}

export interface WorkoutSegmentCreate {
  type?: string;
  name?: string;
  duration?: string;
  distance?: string;
  intensity?: string;
  target_pace?: string;
  target_hr?: string;
  target_power?: string;
  rest?: string;
  notes?: string;
}

// ── Plan Template Types ───────────────────────────────────────────────────────

export interface PlanPhaseResponse {
  id: string;
  plan_template_id: string;
  name: string;
  start_week: number;
  end_week: number;
  focus: string | null;
  volume_modifier: number;
  intensity_modifier: number;
  color: string | null;
  notes: string | null;
}

export interface PlanTemplateResponse {
  id: string;
  coach_id: string;
  name: string;
  sport: string;
  goal_race: string | null;
  duration_weeks: number;
  difficulty: string | null;
  notes: string | null;
  source: string;
  phases: PlanPhaseResponse[];
  created_at: string;
  updated_at: string;
}

export interface PlanTemplateListResponse {
  items: PlanTemplateResponse[];
  total: number;
}

export interface PlanTemplateCreate {
  name: string;
  sport: string;
  goal_race?: string;
  duration_weeks: number;
  difficulty?: string;
  notes?: string;
  phases?: Partial<PlanPhaseResponse>[];
}

export interface PlanPhaseCreate {
  name: string;
  start_week: number;
  end_week: number;
  focus?: string;
  volume_modifier?: number;
  intensity_modifier?: number;
  color?: string;
  notes?: string;
}

// ── Plan Instance Summary Types ───────────────────────────────────────────────

export interface PlanInstanceSummaryPhase {
  name: string;
  start_week: number;
  end_week: number;
  focus: string | null;
  workouts: number;
}

export interface PlanInstanceSummaryResponse {
  total_weeks: number;
  total_workouts: number;
  workouts_per_sport: Record<string, number>;
  estimated_training_hours: number;
  phase_breakdown: PlanInstanceSummaryPhase[];
}



export interface PlanInstanceResponse {
  id: string;
  plan_template_id: string;
  athlete_id: string;
  start_date: string | null;
  end_date: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface PlanInstanceCreate {
  plan_template_id: string;
  athlete_id: string;
  start_date?: string;
}

export interface CalendarEntryResponse {
  id: string;
  plan_instance_id: string;
  workout_template_id: string;
  scheduled_date: string;
  scheduled_time: string | null;
  status: string;
  notes: string | null;
  completed_activity_id: string | null;
  created_at: string;
  updated_at: string;
  template: {
    name: string;
    sport: string;
    stimulus: string | null;
    duration: string | null;
    difficulty: string | null;
    tags: string[];
    segments: WorkoutSegmentResponse[];
  };
}

export interface CalendarEntryCreate {
  workout_template_id: string;
  scheduled_date: string;
  scheduled_time?: string;
  notes?: string;
}

export interface CalendarEntryUpdate {
  scheduled_date?: string;
  scheduled_time?: string;
  overrides?: Record<string, unknown>;
  notes?: string;
  status?: string;
}

// ── Knowledge Types ───────────────────────────────────────────────────────────

export interface KnowledgeEntryResponse {
  id: string;
  coach_id: string;
  title: string;
  sport: string | null;
  tags: string[];
  content: string;
  source: string | null;
  created_at: string;
  updated_at: string;
}

export interface KnowledgeEntryCreate {
  title: string;
  sport?: string;
  tags?: string[];
  content: string;
  source?: string;
}

// ── WeekTemplate Types ────────────────────────────────────────────────────────

export interface WeekTemplateResponse {
  id: string;
  coach_id: string;
  name: string;
  sport: string;
  phase: string | null;
  target_volume: string | null;
  intensity_distribution: string | null;
  notes: string | null;
  days: Record<string, string[]>;
  created_at: string;
  updated_at: string;
}

export interface WeekTemplateListResponse {
  items: WeekTemplateResponse[];
  total: number;
}

export interface WeekTemplateCreate {
  name: string;
  sport: string;
  phase?: string;
  target_volume?: string;
  intensity_distribution?: string;
  notes?: string;
  days?: Record<string, string[]>;
}

export interface WeekTemplateUpdate {
  name?: string;
  sport?: string;
  phase?: string;
  target_volume?: string;
  intensity_distribution?: string;
  notes?: string;
  days?: Record<string, string[]>;
}

// ── CoachAthlete Types ────────────────────────────────────────────────────────

export interface CoachAthleteItem {
  id: string;
  name: string | null;
  email: string | null;
  sport: string | null;
  status: 'active' | 'inactive';
  last_activity_date: string | null;
  active_plan_id: string | null;
  active_plan_name: string | null;
  can_edit: boolean;
}

// ── WorkoutTemplate API ───────────────────────────────────────────────────────

export const fetchWorkoutTemplates = async (params?: {
  sport?: string;
  difficulty?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<WorkoutTemplateListResponse> => {
  try {
    log.debug("TEMPLATES", "fetchWorkoutTemplates", params);
    const r = await coachTemplatesApi.listWorkouts(params);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to fetch workout templates", err);
    throw err;
  }
};

export const fetchWorkoutTemplate = async (id: string): Promise<WorkoutTemplateResponse> => {
  try {
    log.debug("TEMPLATES", "fetchWorkoutTemplate", id);
    const r = await coachTemplatesApi.getWorkout(id);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to fetch workout template", id, err);
    throw err;
  }
};

export const createWorkoutTemplate = async (payload: WorkoutTemplateCreate): Promise<WorkoutTemplateResponse> => {
  try {
    log.debug("TEMPLATES", "createWorkoutTemplate", payload.name);
    const r = await coachTemplatesApi.createWorkout(payload as unknown as Record<string, unknown>);
    log.debug("TEMPLATES", "createWorkoutTemplate done", r.data.id);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to create workout template", payload.name, err);
    throw err;
  }
};

export const updateWorkoutTemplate = async (id: string, payload: WorkoutTemplateUpdate): Promise<WorkoutTemplateResponse> => {
  try {
    log.debug("TEMPLATES", "updateWorkoutTemplate", id);
    const r = await coachTemplatesApi.updateWorkout(id, payload as Record<string, unknown>);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to update workout template", id, err);
    throw err;
  }
};

export const deleteWorkoutTemplate = async (id: string): Promise<void> => {
  try {
    log.debug("TEMPLATES", "deleteWorkoutTemplate", id);
    await coachTemplatesApi.deleteWorkout(id);
  } catch (err) {
    log.error("TEMPLATES", "Failed to delete workout template", id, err);
    throw err;
  }
};

export const duplicateWorkoutTemplate = async (id: string): Promise<WorkoutTemplateResponse> => {
  try {
    log.debug("TEMPLATES", "duplicateWorkoutTemplate", id);
    const r = await coachTemplatesApi.duplicateWorkout(id);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to duplicate workout template", id, err);
    throw err;
  }
};

export const addWorkoutSegment = async (templateId: string, payload: WorkoutSegmentCreate): Promise<WorkoutSegmentResponse> => {
  try {
    const r = await coachTemplatesApi.addSegment(templateId, payload as Record<string, unknown>);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to add segment", templateId, err);
    throw err;
  }
};

export const updateWorkoutSegment = async (
  templateId: string,
  segmentId: string,
  payload: WorkoutSegmentCreate,
): Promise<WorkoutSegmentResponse> => {
  try {
    const r = await coachTemplatesApi.updateSegment(templateId, segmentId, payload as Record<string, unknown>);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to update segment", templateId, segmentId, err);
    throw err;
  }
};

export const deleteWorkoutSegment = async (templateId: string, segmentId: string): Promise<void> => {
  try {
    await coachTemplatesApi.deleteSegment(templateId, segmentId);
  } catch (err) {
    log.error("TEMPLATES", "Failed to delete segment", templateId, segmentId, err);
    throw err;
  }
};

export const reorderWorkoutSegments = async (templateId: string, orderedIds: string[]): Promise<WorkoutSegmentResponse[]> => {
  try {
    const r = await coachTemplatesApi.reorderSegments(templateId, orderedIds);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to reorder segments", templateId, err);
    throw err;
  }
};

// ── PlanTemplate API ──────────────────────────────────────────────────────────

export const fetchPlanTemplates = async (params?: {
  sport?: string;
  difficulty?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<PlanTemplateListResponse> => {
  try {
    log.debug("TEMPLATES", "fetchPlanTemplates", params);
    const r = await coachTemplatesApi.listPlans(params);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to fetch plan templates", err);
    throw err;
  }
};

export const fetchPlanTemplate = async (id: string): Promise<PlanTemplateResponse> => {
  try {
    const r = await coachTemplatesApi.getPlan(id);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to fetch plan template", id, err);
    throw err;
  }
};

export const createPlanTemplate = async (payload: PlanTemplateCreate): Promise<PlanTemplateResponse> => {
  try {
    log.debug("TEMPLATES", "createPlanTemplate", payload.name);
    const r = await coachTemplatesApi.createPlan(payload as unknown as Record<string, unknown>);
    log.debug("TEMPLATES", "createPlanTemplate done", r.data.id);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to create plan template", payload.name, err);
    throw err;
  }
};

export const updatePlanTemplate = async (id: string, payload: Partial<PlanTemplateCreate>): Promise<PlanTemplateResponse> => {
  try {
    const r = await coachTemplatesApi.updatePlan(id, payload as Record<string, unknown>);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to update plan template", id, err);
    throw err;
  }
};

export const deletePlanTemplate = async (id: string): Promise<void> => {
  try {
    await coachTemplatesApi.deletePlan(id);
  } catch (err) {
    log.error("TEMPLATES", "Failed to delete plan template", id, err);
    throw err;
  }
};

export const duplicatePlanTemplate = async (id: string): Promise<PlanTemplateResponse> => {
  try {
    const r = await coachTemplatesApi.duplicatePlan(id);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to duplicate plan template", id, err);
    throw err;
  }
};

export const addPlanPhase = async (templateId: string, payload: PlanPhaseCreate): Promise<PlanPhaseResponse> => {
  try {
    const r = await coachTemplatesApi.addPhase(templateId, payload as unknown as Record<string, unknown>);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to add plan phase", templateId, err);
    throw err;
  }
};

export const updatePlanPhase = async (templateId: string, phaseId: string, payload: Partial<PlanPhaseCreate>): Promise<PlanPhaseResponse> => {
  try {
    const r = await coachTemplatesApi.updatePhase(templateId, phaseId, payload as Record<string, unknown>);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to update plan phase", templateId, phaseId, err);
    throw err;
  }
};

export const deletePlanPhase = async (templateId: string, phaseId: string): Promise<void> => {
  try {
    await coachTemplatesApi.deletePhase(templateId, phaseId);
  } catch (err) {
    log.error("TEMPLATES", "Failed to delete plan phase", templateId, phaseId, err);
    throw err;
  }
};

// ── PlanInstance API ──────────────────────────────────────────────────────────

export const fetchPlanInstances = async (params?: {
  athlete_id?: string;
  status?: string;
}): Promise<{ items: PlanInstanceResponse[]; total: number }> => {
  try {
    const r = await coachTemplatesApi.listInstances(params);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to fetch plan instances", err);
    throw err;
  }
};

export const createPlanInstance = async (payload: PlanInstanceCreate): Promise<PlanInstanceResponse> => {
  try {
    log.debug("TEMPLATES", "createPlanInstance", payload.plan_template_id, payload.athlete_id);
    const r = await coachTemplatesApi.createInstance(payload as unknown as Record<string, unknown>);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to create plan instance", payload.plan_template_id, err);
    throw err;
  }
};

export const deletePlanInstance = async (id: string): Promise<void> => {
  try {
    await coachTemplatesApi.deleteInstance(id);
  } catch (err) {
    log.error("TEMPLATES", "Failed to delete plan instance", id, err);
    throw err;
  }
};

export const fetchPlanInstanceSummary = async (instanceId: string): Promise<PlanInstanceSummaryResponse> => {
  try {
    const r = await coachTemplatesApi.getInstanceSummary(instanceId);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to fetch plan instance summary", instanceId, err);
    throw err;
  }
};

export const fetchCalendarRange = async (
  instanceId: string,
  startDate: string,
  endDate: string,
): Promise<{ entries: CalendarEntryResponse[]; total: number }> => {
  try {
    const r = await coachTemplatesApi.getCalendarRange(instanceId, startDate, endDate);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to fetch calendar range", instanceId, startDate, endDate, err);
    throw err;
  }
};

export const addCalendarEntry = async (instanceId: string, payload: CalendarEntryCreate): Promise<CalendarEntryResponse> => {
  try {
    const r = await coachTemplatesApi.addCalendarEntry(instanceId, payload as unknown as Record<string, unknown>);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to add calendar entry", instanceId, err);
    throw err;
  }
};

export const updateCalendarEntry = async (
  instanceId: string,
  workoutInstanceId: string,
  payload: CalendarEntryUpdate,
): Promise<CalendarEntryResponse> => {
  try {
    const r = await coachTemplatesApi.updateCalendarEntry(instanceId, workoutInstanceId, payload as Record<string, unknown>);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to update calendar entry", instanceId, workoutInstanceId, err);
    throw err;
  }
};

export const deleteCalendarEntry = async (instanceId: string, workoutInstanceId: string): Promise<void> => {
  try {
    await coachTemplatesApi.deleteCalendarEntry(instanceId, workoutInstanceId);
  } catch (err) {
    log.error("TEMPLATES", "Failed to delete calendar entry", instanceId, workoutInstanceId, err);
    throw err;
  }
};

export const bulkAddCalendarEntries = async (
  instanceId: string,
  entries: { workout_template_id: string; scheduled_date: string; scheduled_time?: string }[],
): Promise<{ created: number; entries: CalendarEntryResponse[] }> => {
  try {
    const r = await coachTemplatesApi.bulkAddCalendarEntries(instanceId, entries as Record<string, unknown>[]);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to bulk add calendar entries", instanceId, err);
    throw err;
  }
};

// ── Knowledge API ─────────────────────────────────────────────────────────────

export const fetchKnowledgeEntries = async (params?: {
  sport?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: KnowledgeEntryResponse[]; total: number }> => {
  try {
    const r = await coachTemplatesApi.listKnowledge(params);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to fetch knowledge entries", err);
    throw err;
  }
};

export const createKnowledgeEntry = async (payload: KnowledgeEntryCreate): Promise<KnowledgeEntryResponse> => {
  try {
    const r = await coachTemplatesApi.createKnowledge(payload as unknown as Record<string, unknown>);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to create knowledge entry", err);
    throw err;
  }
};

export const updateKnowledgeEntry = async (id: string, payload: Partial<KnowledgeEntryCreate>): Promise<KnowledgeEntryResponse> => {
  try {
    const r = await coachTemplatesApi.updateKnowledge(id, payload as Record<string, unknown>);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to update knowledge entry", id, err);
    throw err;
  }
};

export const deleteKnowledgeEntry = async (id: string): Promise<void> => {
  try {
    await coachTemplatesApi.deleteKnowledge(id);
  } catch (err) {
    log.error("TEMPLATES", "Failed to delete knowledge entry", id, err);
    throw err;
  }
};

// ── WeekTemplate API ──────────────────────────────────────────────────────────

export const fetchWeekTemplates = async (params?: {
  sport?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<WeekTemplateListResponse> => {
  try {
    const r = await coachTemplatesApi.listWeeks(params);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to fetch week templates", err);
    throw err;
  }
};

export const createWeekTemplate = async (payload: WeekTemplateCreate): Promise<WeekTemplateResponse> => {
  try {
    const r = await coachTemplatesApi.createWeek(payload as unknown as Record<string, unknown>);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to create week template", err);
    throw err;
  }
};

export const updateWeekTemplate = async (id: string, payload: WeekTemplateUpdate): Promise<WeekTemplateResponse> => {
  try {
    const r = await coachTemplatesApi.updateWeek(id, payload as Record<string, unknown>);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to update week template", id, err);
    throw err;
  }
};

export const deleteWeekTemplate = async (id: string): Promise<void> => {
  try {
    await coachTemplatesApi.deleteWeek(id);
  } catch (err) {
    log.error("TEMPLATES", "Failed to delete week template", id, err);
    throw err;
  }
};

export const duplicateWeekTemplate = async (id: string): Promise<WeekTemplateResponse> => {
  try {
    const r = await coachTemplatesApi.duplicateWeek(id);
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to duplicate week template", id, err);
    throw err;
  }
};

// ── CoachAthlete API ──────────────────────────────────────────────────────────

export const fetchCoachAthletes = async (): Promise<CoachAthleteItem[]> => {
  try {
    const r = await coachTemplatesApi.listAthletes();
    return r.data;
  } catch (err) {
    log.error("TEMPLATES", "Failed to fetch coach athletes", err);
    throw err;
  }
};
